import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../services/api';
import { fmtDateTZ } from '../../utils/date';

// Onglet Argent → Stripe : lecture seule des infos du compte Stripe (solde,
// virements bancaires, ventes 30 jours, comptes Connect des partenaires).
const eur = (n) => `${(Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const PAYOUT_STATUS = {
  paid: { label: 'Versé', tone: 'ok' },
  pending: { label: 'En attente', tone: 'warn' },
  in_transit: { label: 'En route', tone: 'warn' },
  canceled: { label: 'Annulé', tone: 'bad' },
  failed: { label: 'Échoué', tone: 'bad' },
};

export default function StripeTab({ P }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tones = { ok: P.green, warn: P.orange, bad: P.red };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/stripe/overview');
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || 'Impossible de lire les infos Stripe.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const card = { bgcolor: P.surface, border: `1px solid ${P.line}`, borderRadius: '12px', p: 2 };
  const label = { fontSize: '0.78rem', fontWeight: 500, color: P.soft, mb: 0.5 };
  const big = { fontSize: '1.5rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: P.text };
  const th = { color: P.soft, fontWeight: 700, fontSize: '0.75rem', borderBottom: `1px solid ${P.line}`, bgcolor: P.bg };
  const td = { fontSize: '0.84rem', color: P.text, borderBottom: `1px solid ${P.lineFine}` };

  if (loading && !data) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress size={26} sx={{ color: P.purple }} /></Box>;
  }

  const s = data?.sales30d || {};
  const Pill = ({ ok, children }) => (
    <Box component="span" sx={{
      display: 'inline-block', px: 1.2, py: 0.3, borderRadius: '999px', fontSize: '0.74rem', fontWeight: 700,
      bgcolor: ok ? 'rgba(29,158,117,0.12)' : 'rgba(239,68,68,0.10)', color: ok ? '#0F6E56' : '#A32D2D',
    }}>{children}</Box>
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={1} flexWrap="wrap">
        <Typography sx={{ fontSize: '0.84rem', color: P.soft }}>
          Données lues en direct sur Stripe {data?.livemode ? '(compte réel)' : '(mode test)'} · lecture seule
        </Typography>
        <Button onClick={load} disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          sx={{ bgcolor: '#EDE8F2', border: `2px solid ${P.text}`, color: P.text, borderRadius: '999px', px: 2, fontWeight: 700, textTransform: 'none' }}>
          Actualiser
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}

      {data && (
        <>
          {/* Solde */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' }, gap: 1.5, mb: 3 }}>
            <Box sx={card}><Typography sx={label}>Disponible</Typography><Typography sx={{ ...big, color: P.purple }}>{eur(data.balance?.available)}</Typography></Box>
            <Box sx={card}><Typography sx={label}>En attente</Typography><Typography sx={big}>{eur(data.balance?.pending)}</Typography></Box>
            <Box sx={card}><Typography sx={label}>Encaissé · 30 j</Typography><Typography sx={big}>{eur(s.charges)}</Typography><Typography sx={{ fontSize: '0.74rem', color: P.soft }}>{s.chargeCount || 0} paiement{s.chargeCount > 1 ? 's' : ''}</Typography></Box>
            <Box sx={card}><Typography sx={label}>Net après frais · 30 j</Typography><Typography sx={big}>{eur(s.net)}</Typography><Typography sx={{ fontSize: '0.74rem', color: P.soft }}>frais Stripe {eur(s.fees)}</Typography></Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 1.5, mb: 3 }}>
            <Box sx={card}><Typography sx={label}>Remboursé · 30 j</Typography><Typography sx={big}>{eur(s.refunds)}</Typography><Typography sx={{ fontSize: '0.74rem', color: P.soft }}>{s.refundCount || 0} remboursement{s.refundCount > 1 ? 's' : ''}</Typography></Box>
            <Box sx={card}><Typography sx={label}>Reversé aux partenaires · 30 j</Typography><Typography sx={big}>{eur(s.transfers)}</Typography></Box>
            <Box sx={card}><Typography sx={label}>Paiements refusés · 30 j</Typography><Typography sx={{ ...big, color: s.failedPayments ? P.red : P.text }}>{s.failedPayments || 0}</Typography></Box>
          </Box>
          {s.truncated && (
            <Typography sx={{ fontSize: '0.75rem', color: P.soft, mt: -2, mb: 3 }}>
              Plus de 100 mouvements sur 30 jours : seuls les 100 plus récents sont comptés.
            </Typography>
          )}

          {/* Virements bancaires */}
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Virements vers ton compte bancaire</Typography>
          <Box sx={{ ...card, p: 0, overflow: 'auto', mb: 3 }}>
            <Table size="small">
              <TableHead><TableRow><TableCell sx={th}>Date d'arrivée</TableCell><TableCell sx={th}>Montant</TableCell><TableCell sx={th}>Statut</TableCell></TableRow></TableHead>
              <TableBody>
                {(data.payouts || []).length === 0 ? (
                  <TableRow><TableCell colSpan={3} sx={{ ...td, color: P.soft, textAlign: 'center', py: 3 }}>Aucun virement pour l'instant.</TableCell></TableRow>
                ) : data.payouts.map((p) => {
                  const st = PAYOUT_STATUS[p.status] || { label: p.status, tone: 'warn' };
                  return (
                    <TableRow key={p.id}>
                      <TableCell sx={td}>{fmtDateTZ(p.arrivalDate, { day: 'numeric', month: 'short', year: 'numeric' })}</TableCell>
                      <TableCell sx={{ ...td, fontWeight: 700 }}>{eur(p.amount)}</TableCell>
                      <TableCell sx={{ ...td, color: tones[st.tone], fontWeight: 700 }}>{st.label}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Comptes Connect */}
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Comptes Stripe des partenaires</Typography>
          <Box sx={{ ...card, p: 0, overflow: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={th}>Partenaire</TableCell><TableCell sx={th}>Type</TableCell>
                <TableCell sx={th}>Paiements</TableCell><TableCell sx={th}>Virements</TableCell><TableCell sx={th}>À compléter</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {(data.accounts || []).length === 0 ? (
                  <TableRow><TableCell colSpan={5} sx={{ ...td, color: P.soft, textAlign: 'center', py: 3 }}>Aucun partenaire n'a encore de compte Stripe.</TableCell></TableRow>
                ) : data.accounts.map((a) => (
                  <TableRow key={a.userId}>
                    <TableCell sx={td}>
                      <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>{a.name || `Compte n° ${a.userId}`}</Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: P.soft }}>{a.email}</Typography>
                    </TableCell>
                    <TableCell sx={td}>{a.userType === 'restaurant_owner' ? 'Restaurant' : a.userType === 'delivery' ? 'Livreur' : 'Chauffeur'}</TableCell>
                    {a.error ? (
                      <TableCell colSpan={3} sx={{ ...td, color: P.red }}>{a.error}</TableCell>
                    ) : (
                      <>
                        <TableCell sx={td}><Pill ok={a.chargesEnabled}>{a.chargesEnabled ? 'Actifs' : 'Bloqués'}</Pill></TableCell>
                        <TableCell sx={td}><Pill ok={a.payoutsEnabled}>{a.payoutsEnabled ? 'Actifs' : 'Bloqués'}</Pill></TableCell>
                        <TableCell sx={{ ...td, color: a.missing ? P.red : P.soft }}>
                          {a.missing ? `${a.missing} info${a.missing > 1 ? 's' : ''} demandée${a.missing > 1 ? 's' : ''}` : 'Rien'}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}
